/**
 * Shared helpers rotasi model AI Gateway — adopsi mekanisme 9router.
 *
 * SATU chain rotasi GLOBAL per klasifikasi (`chat` / `vision`), BUKAN per
 * provider: tiap entri = { provider, model } sehingga model dari provider
 * berbeda bisa saling menggantikan (fallback/round-robin model lintas provider
 * — sama seperti combo 9router yang mencampur `kr/…`, `cbai/…`, `gemini/…`).
 * Provider hanya "pintu + kredensial": baseUrl + keys milik entri tersebut.
 *
 * Dua strategi (setara `fallbackStrategy` 9router):
 *   - "fallback"    : urutan tetap; hanya pindah model saat model aktif gagal.
 *   - "round-robin" : titik mulai digeser tiap request (sticky) supaya beban
 *                     tersebar; pointer maju melewati model yang BENAR-BENAR
 *                     melayani, bukan model yang dijadwalkan (9router issue
 *                     #948 — jangan diulang).
 *
 * Titik mulai round-robin TIDAK disimpan sebagai index angka (index = posisi
 * yang gampang jadi salah begitu chain diubah — reorder/tambah/hapus model di
 * dashboard). Yang disimpan = **anchor**: key entri `provider::model` yang
 * terakhir melayani + sisa `stickyLeft`. Index dihitung ULANG tiap request dari
 * anchor itu (`resolveChainStart`), jadi rotasi tetap lanjut dari model
 * terakhir walau urutan chain diganti-ganti.
 *
 * Dipakai di:
 *   - convex/aiGateway.ts   → runtime chat/vision (baca chain dari settings)
 *   - convex/dashboard.ts   → tulis chain/strategi dari UI dashboard
 *   - dashboard/src/App.tsx → 1 section rotasi + picker model
 *   - scripts/probe-model-rotation.ts → anti-regresi
 */

export const ROTATION_STRATEGIES = ["fallback", "round-robin"] as const;
export type RotationStrategy = (typeof ROTATION_STRATEGIES)[number];

/** Klasifikasi model: chat (tools CRUD) vs vision (baca struk/gambar). */
export const MODEL_CLASSES = ["chat", "vision"] as const;
export type ModelClass = (typeof MODEL_CLASSES)[number];

/** Normalisasi nilai tak dikenal → strategi valid. Default "fallback". */
export function normalizeRotationStrategy(raw: unknown): RotationStrategy {
  return raw === "round-robin" ? "round-robin" : "fallback";
}

/** Sticky default = 1 request per titik mulai (= perilaku sebelum sticky ada). */
export const DEFAULT_STICKY = 1;
/** Batas atas sticky: cukup untuk "pegang satu model selama beberapa request". */
export const MAX_STICKY = 50;
/** Batas sisa hold anchor (sticky - 1): 0 = pointer sudah maju ke model berikutnya. */
export const MAX_STICKY_LEFT = MAX_STICKY - 1;

/**
 * Normalisasi sticky: jumlah REQUEST BERTURUT-TURUT yang memakai titik mulai
 * yang sama sebelum pointer round-robin maju. 1 = maju tiap request.
 */
export function normalizeSticky(raw: unknown): number {
  const value = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(value)) return DEFAULT_STICKY;
  return Math.min(MAX_STICKY, Math.max(1, Math.trunc(value)));
}

/**
 * Normalisasi sisa hold anchor: 0 = pointer sudah maju (request berikutnya pakai
 * model SETELAH model terakhir), > 0 = masih menahan model terakhir.
 */
export function normalizeStickyLeft(raw: unknown): number {
  const value = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_STICKY_LEFT, Math.max(0, Math.trunc(value)));
}

/** Normalisasi anchor (key entri `provider::model`); kosong = belum ada anchor. */
export function normalizeLastModel(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}

/** Setelan rotasi lengkap satu klasifikasi (strategi + sticky). */
export interface RotationSetting {
  strategy: RotationStrategy;
  sticky: number;
}

export function normalizeRotationSetting(
  strategyRaw: unknown,
  stickyRaw: unknown
): RotationSetting {
  return {
    strategy: normalizeRotationStrategy(strategyRaw),
    sticky: normalizeSticky(stickyRaw),
  };
}

// ─── Chain global: entri { provider, model } ────────────────────────────────

/** Satu entri chain rotasi: model + provider pemiliknya (baseUrl + keys). */
export interface ChainEntry {
  provider: string;
  model: string;
}

/** Kunci unik entri (untuk dedupe & pembanding UI). */
export function entryKey(entry: ChainEntry): string {
  return `${entry.provider}::${entry.model}`;
}

/** Label tampil satu entri, meniru combo 9router: `provider/model`. */
export function entryLabel(entry: ChainEntry): string {
  return `${entry.provider}/${entry.model}`;
}

/**
 * Lewati entri yang diminta pemanggil (kunci `provider::model`) dari daftar
 * yang akan dicoba (16 Okt 2026 — akar "ada LLM yang tidak bisa parse JSON").
 *
 * Akar masalah: `botLlm.planMessage` mengulang 3× dengan chain yang SAMA.
 * Model yang membalas konten non-JSON (teks berpikir / prosa) tetap dianggap
 * SUKSES oleh gateway → percobaan 2 & 3 mendarat di model ITU LAGI → percuma,
 * lalu jawaban jatuh ke mode obrolan. Dengan exclude ini, percobaan berikutnya
 * memakai model chain BERIKUTNYA (rotasi benar-benar berpindah).
 *
 * Jaminan: daftar tidak pernah dikosongkan — bila semua entri dikecualikan,
 * daftar semula dipakai (lebih baik mengulang satu model daripada none).
 */
export function skipEntries(
  chain: readonly ChainEntry[],
  avoidKeys: readonly string[]
): ChainEntry[] {
  const avoid = new Set(avoidKeys.filter((key) => typeof key === "string" && key.length > 0));
  if (avoid.size === 0) return [...chain];
  const kept = chain.filter((entry) => !avoid.has(entryKey(entry)));
  return kept.length > 0 ? kept : [...chain];
}

/**
 * Normalisasi chain tersimpan dari DB: buang entri tanpa provider/model,
 * trim, dedupe pasangan provider+model (in-order). Model yang sama di provider
 * berbeda tetap dianggap dua entri berbeda (endpoint/kredensialnya berbeda).
 */
export function normalizeChainEntries(raw: unknown): ChainEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ChainEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const providerRaw = (item as { provider?: unknown })?.provider;
    const modelRaw = (item as { model?: unknown })?.model;
    const provider = typeof providerRaw === "string" ? providerRaw.trim() : "";
    const model = typeof modelRaw === "string" ? modelRaw.trim() : "";
    if (!provider || !model) continue;
    const key = `${provider}::${model}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ provider, model });
  }
  return out;
}

/**
 * Bangun chain dari chain model milik tiap provider (kolom seed provider:
 * `model`+`fallbackModels` / `visionModel`+`visionFallbackModels`) — urut
 * berdasarkan priority, dikelompokkan per provider.
 *
 * Dipakai HANYA untuk migrasi awal: saat `aiGatewaySettings.chatChain` masih
 * kosong (deployment lama), runtime memanggil ini sekali lalu menyimpannya,
 * supaya perilaku sebelum fitur chain global tidak berubah.
 */
export function buildChainFromProviders(
  providers: readonly Record<string, unknown>[],
  kind: ModelClass
): ChainEntry[] {
  const priorityField = kind === "vision" ? "visionPriority" : "priority";
  const ranked = providers
    .filter((p) => p.enabled !== false)
    .map((p) => ({
      provider: p,
      priority: Number(p[priorityField] ?? 99),
    }))
    .sort((a, b) => (Number.isFinite(a.priority) ? a.priority : 99) - (Number.isFinite(b.priority) ? b.priority : 99));

  const out: ChainEntry[] = [];
  for (const { provider } of ranked) {
    out.push(...providerChain(provider, kind));
  }
  return normalizeChainEntries(out);
}

/**
 * Chain `{ provider, model }` milik SATU provider untuk satu klasifikasi,
 * dibaca dari kolom chain seed (`model`+`fallbackModels` / `visionModel`+
 * `visionFallbackModels`). Satu sumber urutan model per provider.
 */
export function providerChain(
  provider: Record<string, unknown>,
  kind: ModelClass
): ChainEntry[] {
  const slug = typeof provider.slug === "string" ? provider.slug : "";
  if (!slug) return [];
  const primaryField = kind === "vision" ? "visionModel" : "model";
  const fallbackField = kind === "vision" ? "visionFallbackModels" : "fallbackModels";
  return buildModelChain(
    provider[primaryField] as string | undefined,
    provider[fallbackField] as string[] | undefined
  ).map((model) => ({ provider: slug, model }));
}

/**
 * Provider default chain rotasi seed: 9router STB (combo campur/vision) +
 * GitHub Copilot. Dipakai `seedRotationChain` untuk memasang konfigurasi awal
 * yang bisa diperiksa walaupun login superadmin belum bisa (env kosong).
 */
export const DEFAULT_ROTATION_PROVIDER_SLUGS = ["9router", "github-copilot"] as const;

/**
 * Apakah seed chain default perlu MENULIS? false bila penanda sudah sama
 * dengan `seedKey` sekarang (kecuali `overwrite: true`). Diuji di probe supaya
 * idempotensi tidak bergantung pada urutan penulisan DB.
 */
export function shouldSeedDefaultChain(
  seededFor: string | undefined,
  seedKey: string,
  overwrite?: boolean
): boolean {
  return overwrite === true || seededFor !== seedKey;
}

/**
 * Chain rotasi default dari SEKUMPULAN provider (urut daftar `slugs`, lalu urut
 * chain model tiap provider) — tanpa melihat `enabled` (provider seed GitHub
 * Copilot sengaja disabled, tapi modelnya tetap ikut konfigurasi awal).
 */
export function buildProviderSlugChain(
  providers: readonly Record<string, unknown>[],
  kind: ModelClass,
  slugs: readonly string[] = DEFAULT_ROTATION_PROVIDER_SLUGS
): ChainEntry[] {
  const bySlug = new Map<string, Record<string, unknown>>();
  for (const provider of providers) {
    const slug = typeof provider.slug === "string" ? provider.slug : "";
    if (slug) bySlug.set(slug, provider);
  }

  const out: ChainEntry[] = [];
  for (const slug of slugs) {
    const provider = bySlug.get(slug);
    if (provider) out.push(...providerChain(provider, kind));
  }
  return normalizeChainEntries(out);
}

/**
 * Nama kolom DB per klasifikasi — satu sumber kebenaran supaya runtime,
 * mutation dashboard, dan UI tidak menebak-nebak sendiri.
 *
 * Chain, cursor, dan sisa sticky disimpan di doc singleton `aiGatewaySettings`
 * (chain global lintas provider).
 */
export interface ChainFields {
  chainField: "chatChain" | "visionChain";
  lastModelField: "chatChainLastModel" | "visionChainLastModel";
  stickyLeftField: "chatChainStickyLeft" | "visionChainStickyLeft";
  strategyField: "chatStrategy" | "visionStrategy";
  stickyField: "chatSticky" | "visionSticky";
}

export function chainFieldsFor(kind: ModelClass): ChainFields {
  return kind === "vision"
    ? {
        chainField: "visionChain",
        lastModelField: "visionChainLastModel",
        stickyLeftField: "visionChainStickyLeft",
        strategyField: "visionStrategy",
        stickyField: "visionSticky",
      }
    : {
        chainField: "chatChain",
        lastModelField: "chatChainLastModel",
        stickyLeftField: "chatChainStickyLeft",
        strategyField: "chatStrategy",
        stickyField: "chatSticky",
      };
}

// ─── Rotasi (generik: chain string maupun entri) ────────────────────────────

/**
 * Tambah nilai ke `out`: trim, buang kosong & duplikat (in-order). Satu sumber
 * dedupe model untuk chain maupun katalog.
 */
function appendUnique(models: string[], values: readonly unknown[]): void {
  for (const raw of values) {
    const model = typeof raw === "string" ? raw.trim() : "";
    if (!model || models.includes(model)) continue;
    models.push(model);
  }
}

/**
 * Bangun daftar model dari primary + fallback: trim, buang kosong, dedupe in-order.
 */
export function buildModelChain(
  primary: string | null | undefined,
  fallbacks?: readonly string[] | null
): string[] {
  const chain: string[] = [];
  appendUnique(chain, [primary, ...(fallbacks ?? [])]);
  return chain;
}

/**
 * Katalog model terdaftar satu provider (dedupe in-order): kolom katalog
 * `models` dulu, lalu chain chat LEGACY (`model`+`fallbackModels`), lalu chain
 * vision LEGACY (`visionModel`+`visionFallbackModels`).
 *
 * Provider hasil seed lama hanya punya kolom chain legacy, bukan `models`.
 * Tanpa fallback ini dashboard menampilkan "Katalog: 0 model" — model yang
 * sudah ada seolah terhapus. SATU sumber katalog, dipakai listProviders /
 * addModels / deleteProviderModel / setRotationChain dan seedProviders.
 */
export function providerCatalog(provider: {
  models?: unknown;
  model?: unknown;
  fallbackModels?: unknown;
  visionModel?: unknown;
  visionFallbackModels?: unknown;
}): string[] {
  const asArray = (value: unknown): readonly unknown[] => (Array.isArray(value) ? value : []);
  const text = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

  const catalog: string[] = [];
  appendUnique(catalog, asArray(provider.models));
  appendUnique(catalog, buildModelChain(text(provider.model), asArray(provider.fallbackModels) as string[]));
  appendUnique(
    catalog,
    buildModelChain(text(provider.visionModel), asArray(provider.visionFallbackModels) as string[])
  );
  return catalog;
}

/** Geser daftar mulai dari `cursor` (round-robin). Cursor aman untuk nilai apa pun. */
export function rotateChain<T>(chain: readonly T[], cursor: number): T[] {
  const n = chain.length;
  if (n <= 1) return [...chain];
  const start = ((Math.trunc(cursor) % n) + n) % n;
  return [...chain.slice(start), ...chain.slice(0, start)];
}

/**
 * Index titik mulai satu request round-robin di dalam `usable` (chain yang
 * dipakai runtime = entri milik provider AKTIF saja).
 *
 * Dinamis: index dihitung dari ANCHOR (`lastModel`) pada chain PENUH, jadi
 * reorder / tambah / hapus model di dashboard tidak membuat rotasi balik ke #1.
 *   - anchor masih ada & masih pakai provider aktif → mulai dari entri itu
 *     (stickyLeft > 0) atau entri SETELAHNYA (stickyLeft = 0).
 *   - provider anchor dimatikan → lanjut ke entri aktif pertama pada/atau
 *     setelah posisi anchor (bungkus ke awal bila perlu).
 *   - belum ada anchor (chain baru) → mulai dari awal chain.
 *
 * Selalu mengembalikan index VALID di `usable` (0..usable.length-1) supaya
 * konsumen (badge dashboard `mulai #n`) tidak perlu menghitung modulo sendiri.
 */
export function resolveChainStart(
  chain: readonly ChainEntry[],
  usable: readonly ChainEntry[],
  lastModel: string,
  stickyLeft: unknown
): number {
  if (usable.length <= 1) return 0;
  const key = normalizeLastModel(lastModel);
  if (!key || chain.length === 0) return 0;
  const position = chain.findIndex((entry) => entryKey(entry) === key);
  if (position < 0) return 0;

  // hold  → pemindaian mulai DARI posisi anchor (inklusif, model terakhir dipakai lagi)
  // maju  → lewati anchor, mulai dari entri berikutnya di chain
  const from = normalizeStickyLeft(stickyLeft) > 0 ? 0 : 1;
  for (let step = from; step < chain.length + from; step++) {
    const candidate = entryKey(chain[(position + step) % chain.length]);
    const index = usable.findIndex((entry) => entryKey(entry) === candidate);
    if (index >= 0) return index;
  }
  return 0;
}

/** Keadaan anchor rotasi yang disimpan di DB (per klasifikasi). */
export interface ChainRunState {
  lastModel: string;
  stickyLeft: number;
}

/**
 * Keadaan anchor setelah satu request round-robin selesai:
 *   - ada model melayani → anchor = model itu ("model terakhir digunakan");
 *     sisa hold dikurangi 1, atau di-reset ke `sticky - 1` kalau request tadi
 *     baru maju (pointer bergeser).
 *   - semua model gagal → anchor = titik mulai yang dicoba, hold 0 → request
 *     berikutnya mulai dari entri SETELAHNYA (tidak mentok model rusak).
 * Sticky tidak berlaku untuk `fallback` (urutan memang tetap) — pemanggil yang
 * memutuskan kapan helper ini dipakai.
 */
export function nextChainState(
  served: ChainEntry | null,
  startedFrom: ChainEntry | null,
  state: { lastModel?: string; stickyLeft?: unknown },
  sticky: unknown
): ChainRunState {
  const window = normalizeSticky(sticky);
  const left = normalizeStickyLeft(state.stickyLeft);
  if (served) {
    return {
      lastModel: entryKey(served),
      stickyLeft: left > 0 ? left - 1 : window - 1,
    };
  }
  return {
    lastModel: startedFrom ? entryKey(startedFrom) : normalizeLastModel(state.lastModel),
    stickyLeft: 0,
  };
}

/**
 * Anchor setelah entri `removedKey` dihapus dari `chain` (chain SEBELUM hapus).
 *
 * Anchor yang masih ada tidak diubah. Kalau yang dihapus = anchor, anchor
 * dipindah ke entri SEBELUM penerusnya supaya rotasi berikutnya mulai dari
 * penerus yang sama — bukan balik ke #1.
 */
export function retargetChainAnchor(
  chain: readonly ChainEntry[],
  removedKey: string,
  anchorKey: string
): string {
  const anchor = normalizeLastModel(anchorKey);
  if (!anchor || anchor !== removedKey) return anchor;
  const position = chain.findIndex((entry) => entryKey(entry) === removedKey);
  if (position < 0 || chain.length <= 1) return "";

  const successor = entryKey(chain[(position + 1) % chain.length]);
  const remaining = chain.filter((entry) => entryKey(entry) !== removedKey);
  const at = remaining.findIndex((entry) => entryKey(entry) === successor);
  if (at < 0) return "";
  return entryKey(remaining[(at - 1 + remaining.length) % remaining.length]);
}
